import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { User, Company, Permission, FieldPermission, Notification, Theme, MenuLayout, BranchInfo, BranchContext } from '@atlas/shared-types';

export interface GlobalState {
  // Auth
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;

  // Company
  company: Company | null;

  // Permissions
  permissions: Permission[];
  fieldPermissions: FieldPermission[];

  // Modules
  enabledModules: string[] | null;

  // Branch
  currentBranch: BranchInfo | null;
  effectiveBranches: BranchInfo[];
  hasAllBranches: boolean;
  primaryBranchId: string | null;

  // UI
  theme: Theme;
  sidebarCollapsed: boolean;
  notifications: Notification[];
  menuLayout: MenuLayout | null;
  menuEditMode: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setAccessToken: (token: string | null) => void;
  setCompany: (company: Company | null) => void;
  setPermissions: (permissions: Permission[]) => void;
  setFieldPermissions: (permissions: FieldPermission[]) => void;
  setEnabledModules: (modules: string[]) => void;
  setTheme: (theme: Theme) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  addNotification: (notification: Notification) => void;
  markNotificationRead: (id: string) => void;
  clearNotifications: () => void;
  setLoading: (loading: boolean) => void;
  setMenuLayout: (layout: MenuLayout | null) => void;
  setMenuEditMode: (editing: boolean) => void;
  setCurrentBranch: (branch: BranchInfo | null) => void;
  setBranchContext: (context: BranchContext) => void;
  reset: () => void;
}

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  accessToken: null,
  company: null,
  permissions: [],
  fieldPermissions: [],
  enabledModules: null,
  theme: 'system' as Theme,
  sidebarCollapsed: false,
  notifications: [],
  menuLayout: null,
  menuEditMode: false,
  currentBranch: null,
  effectiveBranches: [] as BranchInfo[],
  hasAllBranches: false,
  primaryBranchId: null,
};

export const useGlobalStore = create<GlobalState>()(
  immer((set) => ({
    ...initialState,

    setUser: (user) =>
      set((state) => {
        state.user = user;
        state.isAuthenticated = user !== null;
      }),

    setAccessToken: (token) =>
      set((state) => {
        state.accessToken = token;
      }),

    setCompany: (company) =>
      set((state) => {
        state.company = company;
      }),

    setPermissions: (permissions) =>
      set((state) => {
        state.permissions = permissions;
      }),

    setFieldPermissions: (permissions) =>
      set((state) => {
        state.fieldPermissions = permissions;
      }),

    setEnabledModules: (modules) =>
      set((state) => {
        state.enabledModules = modules;
      }),

    setTheme: (theme) =>
      set((state) => {
        state.theme = theme;
      }),

    toggleSidebar: () =>
      set((state) => {
        state.sidebarCollapsed = !state.sidebarCollapsed;
      }),

    setSidebarCollapsed: (collapsed) =>
      set((state) => {
        state.sidebarCollapsed = collapsed;
      }),

    addNotification: (notification) =>
      set((state) => {
        state.notifications.unshift(notification);
      }),

    markNotificationRead: (id) =>
      set((state) => {
        const notif = state.notifications.find((n) => n.id === id);
        if (notif) notif.isRead = true;
      }),

    clearNotifications: () =>
      set((state) => {
        state.notifications = [];
      }),

    setLoading: (loading) =>
      set((state) => {
        state.isLoading = loading;
      }),

    setMenuLayout: (layout) =>
      set((state) => {
        state.menuLayout = layout;
      }),

    setMenuEditMode: (editing) =>
      set((state) => {
        state.menuEditMode = editing;
      }),

    setCurrentBranch: (branch) =>
      set((state) => {
        state.currentBranch = branch;
      }),

    setBranchContext: (context) =>
      set((state) => {
        state.currentBranch = context.currentBranch;
        state.effectiveBranches = context.effectiveBranches;
        state.hasAllBranches = context.hasAllBranches;
        state.primaryBranchId = context.primaryBranchId;
      }),

    reset: () => set(initialState),
  }))
);
